import { Plus } from 'lucide-react';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { Tourney } from 'modules/tournaments/domain/interfaces/tourney';
import { clearSelectedChampionship } from 'modules/tournaments/store/championship/actions';
import FieldBackground from 'modules/tournaments/ui/components/field-background';
import { TournamentsBody } from 'modules/tournaments/ui/components/tourneys-body/tournaments-body';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { theme } from 'styles/theme/theme';
import UDButton from 'ud-ui/components/button';
import UDHeaderTitle from 'ud-ui/components/header-title';
import { useMainLayout } from 'ud-ui/layout/main/context';

import TournamentsFilters from '../components/tourneys-filters';
import { useTourneysList } from '../hooks/use-tourneys-list';

const TournamentsListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setHeaderContent } = useMainLayout();
  const { info, isLoading: isLoadingOrganizer } = useOrganizer();

  const { tourneys, years, filters, changeFilter, isLoading, pages } = useTourneysList();

  const goToTournamentCreate = useCallback(() => {
    dispatch(clearSelectedChampionship());
    navigate(Pages.TOURNEYS.CREATE);
  }, [dispatch, navigate]);

  const resetFilters = useCallback(() => {
    changeFilter({ search: undefined, year: undefined, page: 1 });
  }, [changeFilter]);

  const goToEdit = useCallback(
    (id: Tourney['id']) => {
      navigate(Pages.TOURNEYS.builders.edit(id));
    },
    [navigate],
  );

  const tourneysWithCity = useMemo(() => {
    return tourneys?.map((tourney) => {
      const tourneyAddress = tourney?.locations?.[0] && {
        city: tourney?.locations?.[0]?.address?.city,
        country: tourney?.locations?.[0]?.address?.country,
      };

      return { ...tourney, address: tourneyAddress ?? info?.address };
    });
  }, [info?.address, tourneys]);

  useEffect(() => {
    setHeaderContent(
      <>
        <UDHeaderTitle>Турниры</UDHeaderTitle>
        <UDButton variant="primary" className="ml-6 flex items-center" onClick={goToTournamentCreate}>
          <Plus size={16} className="mr-2" color={theme.colors.DarkBlue} />
          {t('championships.createTournamentButtonTitle')}
        </UDButton>
      </>,
    );

    return () => {
      setHeaderContent(null);
    };
  }, [goToTournamentCreate, setHeaderContent, t]);

  return (
    <div className={'relative flex h-full flex-col'}>
      <FieldBackground />
      <TournamentsFilters onFilter={changeFilter} filters={filters} years={years} className={'relative z-10'} />
      <TournamentsBody
        filters={filters}
        tourneys={tourneysWithCity}
        pages={pages}
        onDetail={goToEdit}
        onPageChange={(page) => changeFilter({ page })}
        onResetFilters={resetFilters}
        isLoading={isLoading || isLoadingOrganizer}
      />
    </div>
  );
};

export default TournamentsListPage;
